import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Bot, 
  Send, 
  Upload, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  Image,
  Link,
  Trash2,
  Sparkles,
  Rocket,
  X,
  Plus,
  CreditCard,
  DollarSign,
  Wand2,
  TrendingUp,
  Target,
  Lightbulb
} from "lucide-react";
import { FaMeta, FaMicrosoft, FaLinkedin } from "react-icons/fa6";
import { FcGoogle } from "react-icons/fc";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlatformIcon = ({ platform, className = "w-5 h-5" }) => {
  switch (platform) {
    case "meta": return <FaMeta className={cn(className, "text-[#1877F2]")} />;
    case "google": return <FcGoogle className={className} />;
    case "bing": return <FaMicrosoft className={cn(className, "text-[#00A4EF]")} />;
    case "linkedin": return <FaLinkedin className={cn(className, "text-[#0A66C2]")} />;
    default: return null;
  }
};

const PLATFORMS = [
  { id: "meta", name: "Meta Ads", description: "Facebook & Instagram" },
  { id: "google", name: "Google Ads", description: "Search & Display" },
  { id: "bing", name: "Bing Ads", description: "Microsoft Advertising" },
  { id: "linkedin", name: "LinkedIn Ads", description: "B2B Marketing" }
];

const FUNDING_PACKAGES = [
  { amount: 500, label: "Starter", description: "Test across platforms", perPlatform: true },
  { amount: 2000, label: "Growth", description: "Scale your reach", perPlatform: true },
  { amount: 5000, label: "Professional", description: "Full campaign power", perPlatform: true },
  { amount: 10000, label: "Enterprise", description: "Maximum impact", perPlatform: true }
];

const OBJECTIVES = [
  { value: "conversions", label: "Conversions" },
  { value: "traffic", label: "Website Traffic" },
  { value: "awareness", label: "Brand Awareness" },
  { value: "leads", label: "Lead Generation" },
  { value: "engagement", label: "Engagement" }
];

const CTA_OPTIONS = [
  { value: "learn_more", label: "Learn More" },
  { value: "shop_now", label: "Shop Now" },
  { value: "sign_up", label: "Sign Up" },
  { value: "contact_us", label: "Contact Us" },
  { value: "download", label: "Download" },
  { value: "get_quote", label: "Get Quote" },
  { value: "book_now", label: "Book Now" },
  { value: "custom", label: "Custom..." }
];

export function CampaignAgent({ open, onOpenChange, onCampaignsCreated, user }) {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [connectedPlatforms, setConnectedPlatforms] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [creationResult, setCreationResult] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  const isDemo = user?.is_demo === true;
  
  const [formData, setFormData] = useState({
    name: "",
    platforms: [],
    daily_budget: 100,
    total_budget: 3000,
    objective: "conversions",
    target_audience: "",
    headlines: [""],
    descriptions: [""],
    destination_url: "",
    cta_button: "learn_more",
    custom_cta: "",
    media_urls: [],
    media_type: "image",
    initial_funding: 2000,
    industry: "",
    ask_price: ""
  });

  const fetchConnections = useCallback(async () => {
    setLoadingConnections(true);
    try {
      const response = await fetch(`${API}/connections`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        const connected = data.filter(c => c.status === "active").map(c => c.platform);
        setConnectedPlatforms(connected);
        // Pre-select all connected platforms
        setFormData(prev => ({ ...prev, platforms: connected }));
      }
    } catch (error) {
      console.error("Failed to fetch connections:", error);
    } finally {
      setLoadingConnections(false);
    }
  }, []);

  // Fetch connected platforms on open
  useEffect(() => {
    if (open) {
      fetchConnections();
      setAiSuggestions(null);
      setShowSuggestions(false);
    }
  }, [open, fetchConnections]);

  const fetchAISuggestions = async () => {
    if (formData.platforms.length === 0) {
      toast.error("Please select at least one platform first");
      return;
    }

    setLoadingSuggestions(true);
    try {
      const response = await fetch(`${API}/ai/suggestions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          platforms: formData.platforms,
          objective: formData.objective,
          target_audience: formData.target_audience,
          industry: formData.industry
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiSuggestions(data);
        setShowSuggestions(true);
        toast.success(`AI suggestions generated! ${data.tokens_remaining} tokens remaining.`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to get AI suggestions");
      }
    } catch (error) {
      toast.error("Failed to get AI suggestions");
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const applySuggestions = () => {
    if (!aiSuggestions?.suggestions) return;

    const suggestions = aiSuggestions.suggestions;
    
    // Apply budget suggestions
    if (suggestions.budget_allocation?.total_recommended_daily) {
      setFormData(prev => ({
        ...prev,
        daily_budget: suggestions.budget_allocation.total_recommended_daily
      }));
    }

    // Apply targeting suggestions
    if (suggestions.targeting_suggestions?.recommended_audience) {
      setFormData(prev => ({
        ...prev,
        target_audience: suggestions.targeting_suggestions.recommended_audience
      }));
    }

    // Apply creative suggestions
    if (suggestions.creative_recommendations?.headline_suggestions) {
      setFormData(prev => ({
        ...prev,
        headlines: suggestions.creative_recommendations.headline_suggestions.slice(0, 5)
      }));
    }

    if (suggestions.creative_recommendations?.description_suggestions) {
      setFormData(prev => ({
        ...prev,
        descriptions: suggestions.creative_recommendations.description_suggestions.slice(0, 3)
      }));
    }

    if (suggestions.creative_recommendations?.cta_recommendation) {
      const ctaValue = suggestions.creative_recommendations.cta_recommendation.toLowerCase().replace(/\s+/g, '_');
      setFormData(prev => ({
        ...prev,
        cta_button: ctaValue
      }));
    }

    toast.success("AI suggestions applied to your campaign!");
    setShowSuggestions(false);
  };

  const togglePlatform = (platformId) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platformId)
        ? prev.platforms.filter(p => p !== platformId)
        : [...prev.platforms, platformId]
    }));
  };

  const handleMediaUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingMedia(true);
    const uploadedUrls = [...formData.media_urls];

    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);

      try {
        const response = await fetch(`${API}/upload/media`, {
          method: "POST",
          credentials: "include",
          body: fd,
        });

        if (response.ok) {
          const data = await response.json();
          uploadedUrls.push(data.url);
          
          if (file.type.startsWith("video")) {
            setFormData(prev => ({ ...prev, media_type: "video" }));
          }
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setFormData(prev => ({ ...prev, media_urls: uploadedUrls }));
    setUploadingMedia(false);
  };

  const removeMedia = (index) => {
    setFormData(prev => ({
      ...prev,
      media_urls: prev.media_urls.filter((_, i) => i !== index)
    }));
  };

  const addHeadline = () => {
    if (formData.headlines.length < 5) {
      setFormData(prev => ({ ...prev, headlines: [...prev.headlines, ""] }));
    }
  };

  const updateHeadline = (index, value) => {
    const updated = [...formData.headlines];
    updated[index] = value;
    setFormData(prev => ({ ...prev, headlines: updated }));
  };

  const removeHeadline = (index) => {
    if (formData.headlines.length > 1) {
      setFormData(prev => ({
        ...prev,
        headlines: prev.headlines.filter((_, i) => i !== index)
      }));
    }
  };

  const addDescription = () => {
    if (formData.descriptions.length < 3) {
      setFormData(prev => ({ ...prev, descriptions: [...prev.descriptions, ""] }));
    }
  };

  const updateDescription = (index, value) => {
    const updated = [...formData.descriptions];
    updated[index] = value;
    setFormData(prev => ({ ...prev, descriptions: updated }));
  };

  const removeDescription = (index) => {
    if (formData.descriptions.length > 1) {
      setFormData(prev => ({
        ...prev,
        descriptions: prev.descriptions.filter((_, i) => i !== index)
      }));
    }
  };

  const canProceedFromStep1 = () => {
    return formData.platforms.length > 0 && formData.name.trim();
  };

  const canProceedFromStep2 = () => {
    return formData.headlines.some(h => h.trim()) && 
           (formData.media_urls.length > 0 || formData.descriptions.some(d => d.trim()));
  };

  // Get the effective CTA value (custom text or predefined)
  const getEffectiveCta = () => {
    if (formData.cta_button === "custom" && formData.custom_cta.trim()) {
      return formData.custom_cta.trim();
    }
    return formData.cta_button;
  };

  // Create campaigns without payment
  const handleSubmitWithoutPayment = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/campaigns/multi-platform`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...formData,
          cta_button: getEffectiveCta(),
          headlines: formData.headlines.filter(h => h.trim()),
          descriptions: formData.descriptions.filter(d => d.trim())
        })
      });

      if (response.ok) {
        const result = await response.json();
        setCreationResult(result);
        setStep(5); // Show success
        toast.success(`Created ${result.created_count} campaigns!`);
        if (onCampaignsCreated) {
          onCampaignsCreated(result.campaigns);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create campaigns");
      }
    } catch (error) {
      toast.error("Failed to create campaigns");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Create campaigns and process payment
  const handleSubmitWithPayment = async () => {
    setProcessingPayment(true);
    try {
      // First create the campaigns
      const campaignResponse = await fetch(`${API}/campaigns/multi-platform`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...formData,
          cta_button: getEffectiveCta(),
          headlines: formData.headlines.filter(h => h.trim()),
          descriptions: formData.descriptions.filter(d => d.trim())
        })
      });

      if (!campaignResponse.ok) {
        const error = await campaignResponse.json();
        throw new Error(error.detail || "Failed to create campaigns");
      }

      const result = await campaignResponse.json();

      // Use the first campaign for payment redirect
      const firstCampaignId = result.campaigns[0]?.campaign_id || result.group_id;

      // Then create checkout session
      const paymentResponse = await fetch(`${API}/payments/checkout/custom`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          campaign_id: firstCampaignId,
          amount: formData.initial_funding,
          description: `Multi-Platform Campaign: ${formData.name} (${formData.platforms.length} platforms)`,
          origin_url: window.location.origin
        })
      });

      if (!paymentResponse.ok) {
        // Payment failed but campaigns created - show partial success
        setCreationResult({ ...result, payment_failed: true });
        setStep(5);
        toast.warning("Campaigns created but payment failed. You can fund them later.");
        if (onCampaignsCreated) {
          onCampaignsCreated(result.campaigns);
        }
        return;
      }

      const paymentData = await paymentResponse.json();

      // Redirect to Stripe checkout (handle both 'url' and 'checkout_url' keys)
      const checkoutUrl = paymentData.checkout_url || paymentData.url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        // If no checkout URL, show success
        setCreationResult(result);
        setStep(5);
        toast.success(`Created ${result.created_count} campaigns!`);
        if (onCampaignsCreated) {
          onCampaignsCreated(result.campaigns);
        }
      }
    } catch (error) {
      toast.error(error.message || "Failed to create campaigns");
    } finally {
      setProcessingPayment(false);
    }
  };

  const resetAndClose = () => {
    setStep(1);
    setCreationResult(null);
    setFormData({
      name: "",
      platforms: connectedPlatforms,
      daily_budget: 100,
      total_budget: 3000,
      objective: "conversions",
      target_audience: "",
      headlines: [""],
      descriptions: [""],
      destination_url: "",
      cta_button: "learn_more",
      custom_cta: "",
      media_urls: [],
      media_type: "image",
      initial_funding: 2000,
      ask_price: ""
    });
    onOpenChange(false);
  };

  // Calculate per-platform funding
  const perPlatformFunding = formData.platforms.length > 0 
    ? (formData.initial_funding / formData.platforms.length).toFixed(0)
    : formData.initial_funding;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="border-b border-white/10 pb-4">
          <DialogTitle className="font-heading text-2xl flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            Campaign Agent
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Create campaigns across all your connected ad platforms at once
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {[
              { num: 1, label: "Platforms" },
              { num: 2, label: "Creative" },
              { num: 3, label: "Review" },
              { num: 4, label: "Payment" },
              { num: 5, label: "Launch" }
            ].map((s, idx) => (
              <div key={s.num} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={cn(
                    "w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors",
                    step >= s.num 
                      ? "bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white" 
                      : "bg-zinc-800 text-zinc-400"
                  )}>
                    {step > s.num ? <CheckCircle className="w-3 h-3" /> : s.num}
                  </div>
                  <span className="text-[9px] text-zinc-400 mt-1">{s.label}</span>
                </div>
                {idx < 4 && (
                  <div className={cn(
                    "w-8 h-0.5 mx-0.5 mb-4 transition-colors",
                    step > s.num ? "bg-violet-500" : "bg-zinc-800"
                  )} />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Platform Selection */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="space-y-2">
                <Label className="text-zinc-300">Campaign Name *</Label>
                <Input
                  placeholder="e.g., Summer Sale 2025"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-black/50 border-white/10 text-white placeholder:text-zinc-500"
                />
              </div>

              <div className="space-y-3">
                <Label className="text-zinc-300">Select Platforms to Launch On *</Label>
                {loadingConnections ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {PLATFORMS.map((platform) => {
                      const isConnected = connectedPlatforms.includes(platform.id);
                      const isSelected = formData.platforms.includes(platform.id);
                      
                      return (
                        <button
                          key={platform.id}
                          onClick={() => isConnected && togglePlatform(platform.id)}
                          disabled={!isConnected}
                          className={cn(
                            "p-4 rounded-xl border-2 text-left transition-all relative",
                            isConnected 
                              ? isSelected
                                ? "border-violet-500 bg-violet-500/10"
                                : "border-white/10 hover:border-white/30 bg-black/30"
                              : "border-white/5 bg-black/20 opacity-50 cursor-not-allowed"
                          )}
                        >
                          {isSelected && (
                            <div className="absolute top-2 right-2">
                              <CheckCircle className="w-5 h-5 text-violet-400" />
                            </div>
                          )}
                          <div className="flex items-center gap-3">
                            <PlatformIcon platform={platform.id} className="w-8 h-8" />
                            <div>
                              <h3 className="font-semibold text-white">{platform.name}</h3>
                              <p className="text-zinc-400 text-xs">{platform.description}</p>
                            </div>
                          </div>
                          {!isConnected && (
                            <Badge variant="outline" className="mt-2 text-xs border-amber-500/30 text-amber-400">
                              Not Connected
                            </Badge>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Daily Budget ($)</Label>
                  <Input
                    type="number"
                    min="10"
                    value={formData.daily_budget}
                    onChange={(e) => setFormData({ ...formData, daily_budget: parseFloat(e.target.value) || 0 })}
                    className="bg-black/50 border-white/10 text-white"
                  />
                  <p className="text-xs text-zinc-400">Split across {formData.platforms.length || 1} platforms</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Ask Price ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="e.g., 50"
                    value={formData.ask_price}
                    onChange={(e) => setFormData({ ...formData, ask_price: parseFloat(e.target.value) || "" })}
                    className="bg-black/50 border-white/10 text-white placeholder:text-zinc-500"
                  />
                  <p className="text-xs text-zinc-400">Revenue per conversion (avg order value)</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300">Objective</Label>
                <Select
                  value={formData.objective}
                  onValueChange={(value) => setFormData({ ...formData, objective: value })}
                >
                  <SelectTrigger className="bg-black/50 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181B] border-white/10">
                    {OBJECTIVES.map((obj) => (
                      <SelectItem key={obj.value} value={obj.value} className="text-white hover:bg-white/10">
                        {obj.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300">Target Audience (Optional)</Label>
                <Input
                  placeholder="e.g., 25-45 year olds interested in fitness"
                  value={formData.target_audience}
                  onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
                  className="bg-black/50 border-white/10 text-white placeholder:text-zinc-500"
                />
              </div>

              {/* AI Suggestions Button */}
              <div className="pt-4 border-t border-white/10">
                <Button
                  onClick={fetchAISuggestions}
                  disabled={loadingSuggestions || formData.platforms.length === 0}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold"
                >
                  {loadingSuggestions ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing past campaigns...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4 mr-2" />
                      Get AI Suggestions (5 tokens)
                    </>
                  )}
                </Button>
                <p className="text-xs text-zinc-400 text-center mt-2">
                  AI will analyze your past campaign performance and suggest optimal settings
                </p>
              </div>

              {/* AI Suggestions Panel */}
              <AnimatePresence>
                {showSuggestions && aiSuggestions?.suggestions && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-white flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-amber-400" />
                          AI Recommendations
                        </h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowSuggestions(false)}
                          className="text-zinc-400 hover:text-white h-7"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>

                      {/* Budget Recommendation */}
                      {aiSuggestions.suggestions.budget_allocation && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm text-amber-300">
                            <DollarSign className="w-4 h-4" />
                            Budget Recommendation
                          </div>
                          <p className="text-white text-sm">
                            Recommended daily budget: <span className="font-bold text-amber-400">${aiSuggestions.suggestions.budget_allocation.total_recommended_daily}</span>
                          </p>
                          {aiSuggestions.suggestions.budget_allocation.by_platform && (
                            <div className="grid grid-cols-2 gap-2 mt-2">
                              {Object.entries(aiSuggestions.suggestions.budget_allocation.by_platform).map(([platform, data]) => (
                                <div key={platform} className="bg-black/30 rounded-lg p-2 text-xs">
                                  <div className="flex items-center gap-1 text-zinc-300 capitalize">
                                    <PlatformIcon platform={platform} className="w-3 h-3" />
                                    {platform}
                                  </div>
                                  <p className="text-amber-400 font-medium">${data.daily_budget} ({data.percentage}%)</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Targeting Recommendation */}
                      {aiSuggestions.suggestions.targeting_suggestions && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm text-amber-300">
                            <Target className="w-4 h-4" />
                            Targeting
                          </div>
                          <p className="text-white text-sm">
                            {aiSuggestions.suggestions.targeting_suggestions.recommended_audience}
                          </p>
                        </div>
                      )}

                      {/* Creative Suggestions Preview */}
                      {aiSuggestions.suggestions.creative_recommendations?.headline_suggestions && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm text-amber-300">
                            <Lightbulb className="w-4 h-4" />
                            Suggested Headlines
                          </div>
                          <div className="space-y-1">
                            {aiSuggestions.suggestions.creative_recommendations.headline_suggestions.slice(0, 3).map((headline, idx) => (
                              <p key={`headline-${idx}-${headline.slice(0,20)}`} className="text-white text-sm bg-black/30 rounded px-2 py-1">
                                {headline}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Expected Performance */}
                      {aiSuggestions.suggestions.expected_performance && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm text-amber-300">
                            <TrendingUp className="w-4 h-4" />
                            Expected Performance
                          </div>
                          <div className="flex gap-4 text-sm">
                            <span className="text-white">
                              ROAS: <span className="text-emerald-400 font-bold">{aiSuggestions.suggestions.expected_performance.estimated_roas}x</span>
                            </span>
                            <span className="text-white">
                              CTR: <span className="text-emerald-400 font-bold">{aiSuggestions.suggestions.expected_performance.estimated_ctr}%</span>
                            </span>
                            <span className="text-zinc-400 text-xs">
                              ({aiSuggestions.suggestions.expected_performance.confidence} confidence)
                            </span>
                          </div>
                        </div>
                      )}

                      <Button
                        onClick={applySuggestions}
                        className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Apply All Suggestions
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {/* Step 2: Creative */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-5"
            >
              {/* Headlines */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-zinc-300">Headlines *</Label>
                  {formData.headlines.length < 5 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={addHeadline}
                      className="text-violet-400 hover:text-violet-300 h-7 text-xs"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add Headline
                    </Button>
                  )}
                </div>
                {formData.headlines.map((headline, idx) => (
                  <div key={`hl-${idx}`} className="flex gap-2">
                    <Input
                      placeholder={`Headline ${idx + 1}`}
                      value={headline}
                      onChange={(e) => updateHeadline(idx, e.target.value)}
                      className="bg-black/50 border-white/10 text-white placeholder:text-zinc-500"
                    />
                    {formData.headlines.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeHeadline(idx)}
                        className="text-zinc-400 hover:text-red-400 shrink-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Descriptions */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-zinc-300">Descriptions</Label>
                  {formData.descriptions.length < 3 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={addDescription}
                      className="text-violet-400 hover:text-violet-300 h-7 text-xs"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add Description
                    </Button>
                  )}
                </div>
                {formData.descriptions.map((desc, idx) => (
                  <div key={`desc-${idx}`} className="flex gap-2">
                    <Textarea
                      placeholder={`Description ${idx + 1}`}
                      value={desc}
                      onChange={(e) => updateDescription(idx, e.target.value)}
                      className="bg-black/50 border-white/10 text-white placeholder:text-zinc-500 min-h-[60px] resize-none"
                    />
                    {formData.descriptions.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeDescription(idx)}
                        className="text-zinc-400 hover:text-red-400 shrink-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Media Upload */}
              <div className="space-y-3">
                <Label className="text-zinc-300">Media (Images/Videos)</Label>
                <div className="border-2 border-dashed border-white/20 rounded-xl p-6 text-center hover:border-violet-500/50 transition-colors">
                  <input
                    type="file"
                    id="agent-media-upload"
                    multiple
                    accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm"
                    onChange={handleMediaUpload}
                    className="hidden"
                  />
                  <label htmlFor="agent-media-upload" className="cursor-pointer">
                    {uploadingMedia ? (
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
                        <span className="text-zinc-400">Uploading...</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <Upload className="w-10 h-10 text-zinc-400" />
                        <span className="text-zinc-300">Click to upload images or videos</span>
                        <span className="text-xs text-zinc-400">JPG, PNG, GIF, WebP, MP4, WebM</span>
                      </div>
                    )}
                  </label>
                </div>
                
                {formData.media_urls.length > 0 && (
                  <div className="grid grid-cols-4 gap-3 mt-3">
                    {formData.media_urls.map((url, idx) => (
                      <div key={`media-${idx}-${url.slice(-10)}`} className="relative group aspect-square rounded-lg overflow-hidden bg-black/50">
                        <img 
                          src={`${BACKEND_URL}${url}`} 
                          alt={`Media ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          onClick={() => removeMedia(idx)}
                          className="absolute top-2 right-2 p-1.5 bg-black/70 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
                        >
                          <Trash2 className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Destination URL & CTA */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Destination URL</Label>
                  <div className="relative">
                    <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      placeholder="https://yourwebsite.com"
                      value={formData.destination_url}
                      onChange={(e) => setFormData({ ...formData, destination_url: e.target.value })}
                      className="bg-black/50 border-white/10 text-white placeholder:text-zinc-500 pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Call-to-Action</Label>
                  <Select
                    value={formData.cta_button}
                    onValueChange={(value) => setFormData({ ...formData, cta_button: value, custom_cta: value === "custom" ? formData.custom_cta : "" })}
                  >
                    <SelectTrigger className="bg-black/50 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#18181B] border-white/10">
                      {CTA_OPTIONS.map((cta) => (
                        <SelectItem key={cta.value} value={cta.value} className="text-white hover:bg-white/10">
                          {cta.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Custom CTA Input */}
              {formData.cta_button === "custom" && (
                <div className="space-y-2">
                  <Label className="text-zinc-300">Custom CTA Text</Label>
                  <Input
                    placeholder="Enter your custom call-to-action"
                    value={formData.custom_cta}
                    onChange={(e) => setFormData({ ...formData, custom_cta: e.target.value })}
                    className="bg-black/50 border-white/10 text-white placeholder:text-zinc-500"
                    maxLength={25}
                  />
                  <p className="text-xs text-zinc-400">{formData.custom_cta.length}/25 characters</p>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="bg-black/40 rounded-xl p-5 border border-white/10 space-y-4">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-violet-400" />
                  Campaign Summary
                </h3>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-zinc-400">Campaign Name</p>
                    <p className="text-white font-medium">{formData.name}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400">Objective</p>
                    <p className="text-white font-medium capitalize">{formData.objective}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400">Daily Budget</p>
                    <p className="text-white font-medium">${formData.daily_budget} total (${(formData.daily_budget / formData.platforms.length).toFixed(2)}/platform)</p>
                  </div>
                  <div>
                    <p className="text-zinc-400">Target Audience</p>
                    <p className="text-white font-medium">{formData.target_audience || "Not specified"}</p>
                  </div>
                </div>
              </div>

              {/* Platforms */}
              <div className="space-y-3">
                <h3 className="font-semibold text-white">Launching on {formData.platforms.length} Platforms</h3>
                <div className="flex flex-wrap gap-2">
                  {formData.platforms.map((platform) => (
                    <div
                      key={platform}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10"
                    >
                      <PlatformIcon platform={platform} className="w-5 h-5" />
                      <span className="text-white capitalize">{platform}</span>
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Creative Preview */}
              <div className="space-y-3">
                <h3 className="font-semibold text-white">Ad Preview</h3>
                <div className="bg-black/40 rounded-xl p-4 border border-white/10">
                  <div className="flex items-start gap-4">
                    {formData.media_urls[0] && (
                      <img 
                        src={`${BACKEND_URL}${formData.media_urls[0]}`}
                        alt="Preview"
                        className="w-24 h-24 rounded-lg object-cover"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-lg">
                        {formData.headlines[0] || "Your headline"}
                      </p>
                      <p className="text-zinc-400 mt-1">
                        {formData.descriptions[0] || "Your description"}
                      </p>
                      {formData.destination_url && (
                        <p className="text-zinc-400 text-sm mt-2 truncate">
                          {formData.destination_url}
                        </p>
                      )}
                      <div className="mt-3">
                        <span className="inline-block px-4 py-1.5 bg-violet-600 text-white text-sm rounded font-medium">
                          {formData.cta_button === "custom" && formData.custom_cta.trim()
                            ? formData.custom_cta.trim()
                            : CTA_OPTIONS.find(c => c.value === formData.cta_button)?.label || "Learn More"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-sm text-amber-300">
                <p className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Creative will be adapted to each platform&apos;s requirements (character limits, CTA formats)
                </p>
              </div>
            </motion.div>
          )}

          {/* Step 4: Payment */}
          {step === 4 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="bg-black/40 rounded-xl p-5 border border-white/10 space-y-3">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-400" />
                  Fund Your Campaigns
                </h3>
                <p className="text-zinc-400 text-sm">
                  Add budget to launch your campaigns across {formData.platforms.length} platforms
                </p>
              </div>

              {/* Funding Packages */}
              <div className="space-y-3">
                <Label className="text-zinc-300">Select Total Campaign Budget</Label>
                <div className="grid grid-cols-2 gap-3">
                  {FUNDING_PACKAGES.map((pkg) => (
                    <button
                      key={pkg.amount}
                      onClick={() => setFormData({ ...formData, initial_funding: pkg.amount })}
                      className={cn(
                        "p-4 rounded-xl border-2 text-left transition-all",
                        formData.initial_funding === pkg.amount
                          ? "border-emerald-500 bg-emerald-500/10"
                          : "border-white/10 hover:border-white/30 bg-black/30"
                      )}
                    >
                      <div className="text-2xl font-bold text-white">${pkg.amount.toLocaleString()}</div>
                      <div className="text-sm font-medium text-zinc-300">{pkg.label}</div>
                      <div className="text-xs text-zinc-400">{pkg.description}</div>
                      <div className="text-xs text-emerald-400 mt-1">
                        ${(pkg.amount / formData.platforms.length).toFixed(0)}/platform
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Amount */}
              <div className="space-y-2">
                <Label className="text-zinc-300">Or enter custom amount</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input
                    type="number"
                    min="100"
                    max="100000"
                    value={formData.initial_funding}
                    onChange={(e) => setFormData({ ...formData, initial_funding: parseInt(e.target.value) || 500 })}
                    className="bg-black/50 border-white/10 text-white pl-10"
                  />
                </div>
                <p className="text-xs text-zinc-400">
                  ${perPlatformFunding} per platform ({formData.platforms.length} platforms selected)
                </p>
              </div>

              {/* Budget Breakdown */}
              <div className="bg-black/40 rounded-xl p-4 border border-white/10">
                <h4 className="text-sm font-medium text-zinc-300 mb-3">Budget Breakdown</h4>
                <div className="space-y-2">
                  {formData.platforms.map((platform) => (
                    <div key={platform} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <PlatformIcon platform={platform} className="w-4 h-4" />
                        <span className="text-white capitalize">{platform}</span>
                      </div>
                      <span className="text-emerald-400 font-medium">${perPlatformFunding}</span>
                    </div>
                  ))}
                  <div className="border-t border-white/10 pt-2 mt-2 flex items-center justify-between font-semibold">
                    <span className="text-zinc-300">Total</span>
                    <span className="text-white">${formData.initial_funding.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-sm text-emerald-300">
                <p className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Secure payment via Stripe. Campaigns will launch immediately after payment.
                </p>
              </div>
            </motion.div>
          )}

          {/* Step 5: Success */}
          {step === 5 && creationResult && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8 space-y-6"
            >
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center">
                <Rocket className="w-10 h-10 text-white" />
              </div>
              
              <div>
                <h2 className="text-2xl font-bold text-white">Campaigns Launched!</h2>
                <p className="text-zinc-400 mt-2">
                  Successfully created {creationResult.created_count} campaigns across your ad platforms
                </p>
              </div>

              <div className="flex flex-wrap justify-center gap-3">
                {creationResult.campaigns.map((campaign) => (
                  <div
                    key={campaign.campaign_id}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30"
                  >
                    <PlatformIcon platform={campaign.platform} className="w-5 h-5" />
                    <span className="text-emerald-300 capitalize">{campaign.platform}</span>
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                  </div>
                ))}
              </div>

              {creationResult.unconnected_platforms?.length > 0 && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-sm text-amber-300">
                  <p>Skipped (not connected): {creationResult.unconnected_platforms.join(", ")}</p>
                </div>
              )}

              {creationResult.payment_failed && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-sm text-amber-300">
                  <p className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Payment was not processed. You can fund your campaigns from the Campaigns page.
                  </p>
                </div>
              )}

              <Button
                onClick={resetAndClose}
                className="bg-white text-black hover:bg-zinc-200 font-semibold px-8"
              >
                Done
              </Button>
            </motion.div>
          )}
        </div>

        {/* Footer with Navigation */}
        {step < 5 && (
          <div className="border-t border-white/10 pt-4 flex justify-between">
            <Button
              variant="ghost"
              onClick={() => step > 1 ? setStep(step - 1) : resetAndClose()}
              className="text-zinc-400 hover:text-white"
            >
              {step === 1 ? "Cancel" : "Back"}
            </Button>
            
            {step < 3 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={step === 1 ? !canProceedFromStep1() : !canProceedFromStep2()}
                className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:from-violet-700 hover:to-fuchsia-700 font-semibold"
              >
                Continue
              </Button>
            ) : step === 3 ? (
              <Button
                onClick={() => setStep(4)}
                className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:from-violet-700 hover:to-fuchsia-700 font-semibold"
              >
                Continue to Payment
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleSubmitWithoutPayment}
                  disabled={isSubmitting || processingPayment}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Launch Without Funding
                </Button>
                <Button
                  onClick={handleSubmitWithPayment}
                  disabled={isSubmitting || processingPayment}
                  className="bg-gradient-to-r from-emerald-600 to-green-600 text-white hover:from-emerald-700 hover:to-green-700 font-semibold"
                >
                  {processingPayment ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Pay ${formData.initial_funding.toLocaleString()} & Launch
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
